import { Button, Heading, Icon } from '@assembly-js/design-system';
import { PostOrPage } from '@tryghost/content-api';
import { useSnackbar } from 'notistack';
import React from 'react';

import { HtmlRenderer } from 'src/components/HTMLRenderer';
import { HeartFilled } from 'src/components/icons';
import { Toast } from 'src/components/Toast';
import { COPILOT_CHANGELOG_LINK } from 'src/constants';
import { useEsc } from 'src/hooks/useEsc';
import LegacyButton from 'src/legacy/components/Button';
import { useUpdateLatestReleaseDismissalMutation } from 'src/services/api/LatestReleaseApi';
import { AlertVariant } from 'src/store/ui/types';

interface WhatsNewModal {
  latestReleasePost: PostOrPage | null;
}

export const WhatsNewModal: React.FC<WhatsNewModal> = ({
  latestReleasePost,
}) => {
  const [updateLatestReleaseDismissal] =
    useUpdateLatestReleaseDismissalMutation();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleUndoDismiss = () => {
    updateLatestReleaseDismissal({
      structFields: { latest_release_dismissed: '' },
    });
  };

  const successDismissSnackbar = React.useCallback(
    (snackbarKey: string) => (
      <div key={snackbarKey}>
        <Toast
          variant={AlertVariant.SUCCESS}
          onClose={() => closeSnackbar(snackbarKey)}
          description="Release update closed"
          actionProps={{
            onClick: () => handleUndoDismiss(),
            children: 'Undo',
            className: 'text-base',
          }}
        />
      </div>
    ),
    [closeSnackbar],
  );

  const handleDismissLatestRelease = async () => {
    if (!latestReleasePost) return;
    const result = await updateLatestReleaseDismissal({
      structFields: {
        latest_release_dismissed: latestReleasePost.id,
      },
    });

    if ('data' in result) {
      enqueueSnackbar('Resource has been dismissed', {
        content: (snackbarKey: string) => successDismissSnackbar(snackbarKey),
      });
    }
  };

  useEsc(() => {
    handleDismissLatestRelease();
  });

  return (
    // Background Overlay
    <div
      className="fixed inset-0 h-screen w-screen bg-black bg-opacity-40 z-modal items-center justify-center hidden lg:flex"
      onClick={handleDismissLatestRelease}
    >
      {/* Modal */}
      <div
        className="w-11/12 h-full max-h-[792px] max-w-[1094px] bg-white overflow-hidden rounded-4 shadow-popover-500"
        onClick={(e) => e.stopPropagation()} // Prevents the modal from closing when clicked inside since the overlay is also listening for clicks
      >
        <div className="flex items-center justify-between py-3 px-4 bg-info-background">
          <div className="flex items-center">
            <HeartFilled className="text-info-text mr-2 h-3 w-3" />
            <Heading size="lg" className="text-info-text">
              {"What's New"}
            </Heading>
          </div>
          <Button
            size="sm"
            label="Close"
            prefixIcon="Close"
            onClick={handleDismissLatestRelease}
            className="bg-white hover:bg-gray-100"
            variant="secondary"
          />
        </div>
        <div
          className="overflow-y-auto px-6"
          style={{ height: 'calc(100% - 52px)' }} // 52px is the height of the header
        >
          <div className="pb-6 pt-2 w-full max-w-[900px] mx-auto">
            <HtmlRenderer html={latestReleasePost?.html || ''} />
            <a
              href={COPILOT_CHANGELOG_LINK}
              target="_blank"
              className="inline-block mt-6"
              rel="noreferrer"
            >
              {/* TODO: Replace with design system button. Current secondary button doesn't support endIcon */}
              <LegacyButton
                htmlId="view-all-releases"
                variant="contained"
                color="secondary"
                endIcon={<Icon icon="ArrowNE" />}
              >
                View all releases
              </LegacyButton>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
